import { useEffect, useState, useCallback, useRef } from 'react'

import { useWebSocketApi } from '@e3dc-react/shell/Hooks'
import { SocketStateModel } from '@e3dc-react/shell/Hooks/use-websocket-api'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import fromDtoToTestResultModelTransformer from '../transformers/from-dto-to-test-result.transformers'
import { HardwareTestResultModel, TriggerHardwareTestData, TriggerHardwareTestParamsData } from '../models'
import { TestResultDto } from '../dto/hardware-test-result.dto'
import { StatusCodes } from '@e3dc-react/shell/Libs/websocket-status-codes'

const SERVICE_ID = '[TRIGGER HARDWARE TEST]'

interface UseTriggerHardwareTestReturnType {
  hardwareTestResult: SocketStateModel<HardwareTestResultModel>
  triggerTest: (params?: TriggerHardwareTestParamsData) => void
  isTriggered: boolean
  finishTesting: () => void
}

const defaultDataForSending = {
  params: null,
}

/** Hook to run hardware test of selected system. */
export const useTriggerHardwareTest = (serialNumber?: string, testId?: string): UseTriggerHardwareTestReturnType => {
  const [canInitialize, setCanInitialize] = useState(false)
  const { enqueueErrorStack } = useSnackbar()
  const { t } = useCustomTranslation()
  const resourcePath = `hardware-tests/storages/${serialNumber}/tests/${testId ?? 'initial'}/ws`
  const dataForSending = useRef<TriggerHardwareTestData>(defaultDataForSending)
  const wasSentOnce = useRef(false)
  const { socketState, webSocket, resetWebsocketToInitial } = useWebSocketApi<HardwareTestResultModel, TestResultDto>({
    endpoint: process.env.REACT_APP_HARDWARE_TESTS_ENDPOINT ?? '',
    apiResourcePath: resourcePath,
    loggerKey: SERVICE_ID,
    transformer: fromDtoToTestResultModelTransformer,
    doNotInitialize: !serialNumber || !canInitialize,
    dependencies: [serialNumber, testId],
  })

  const finishTesting = useCallback(() => {
    resetWebsocketToInitial()
    setCanInitialize(false)
    dataForSending.current = defaultDataForSending
    wasSentOnce.current = false
  }, [resetWebsocketToInitial, setCanInitialize, dataForSending, wasSentOnce])

  useEffect(() => {
    /** The web socket closes automatically when event with result is received. */
    if (socketState.error && socketState.error.code !== StatusCodes.CLOSED_NO_STATUS) {
      enqueueErrorStack(t('system-details.hardware-tests.initial-test.error-message'))
      finishTesting()
    }
  }, [enqueueErrorStack, socketState.error, finishTesting, t])

  useEffect(() => {
    return () => setCanInitialize(false)
  }, [])

  const openWebsocket = useCallback(() => {
    setCanInitialize(true)
    wasSentOnce.current = false
  }, [])

  const triggerTest = (params?: TriggerHardwareTestParamsData): void => {
    dataForSending.current = {
      params: params ?? null,
    }
    openWebsocket()
  }

  useEffect(() => {
    const testRunId = socketState.data?.testRunId
    const state = socketState.data?.state

    if (testRunId && !state && !wasSentOnce.current) {
      webSocket.current?.send(JSON.stringify(dataForSending.current))
      wasSentOnce.current = true
    }
  }, [socketState, webSocket])

  return { hardwareTestResult: socketState, isTriggered: canInitialize, triggerTest, finishTesting }
}
